import { useCallback, useMemo } from 'react'
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ChipsList } from '../../../../../components/chipsList'
import { useContextState } from '../../../../../hooks/theme'
import { isEmail } from '../../../../../utils/validate/validate'
import { InternalUser } from '../../createActionPlan/types'
import { Step3NotificationsProps } from '../types'
import { useStyles } from './step3Notifications.styles'

const Step3Notifications = ({
  data,
  onChange,
  errors,
  internalUserOptions,
  isLoadingInternalUsers,
  emailsRawInput,
  setEmailsRawInput,
}: Step3NotificationsProps) => {
  const { t } = useTranslation()
  const styles = useStyles()
  const { colors } = useContextState()

  const selectedInternalUsers = useMemo(() => {
    return internalUserOptions.filter((user) => data.selectedInternalUsers.includes(user.id))
  }, [internalUserOptions, data.selectedInternalUsers])

  const handleChangeInternalUsers = useCallback(
    (_: unknown, value: InternalUser[]) => {
      onChange({ selectedInternalUsers: value.map((user) => user.id) })
    },
    [onChange]
  )

  const handleRemoveInternalUser = useCallback(
    (option: { value: string; label: string }) => {
      const newSelected = data.selectedInternalUsers.filter((id) => id !== Number(option.value))
      onChange({ selectedInternalUsers: newSelected })
    },
    [data.selectedInternalUsers, onChange]
  )

  const internalUsersAsItems = useMemo(() => {
    return selectedInternalUsers.map((user) => ({
      value: String(user.id),
      label: user.fullName,
    }))
  }, [selectedInternalUsers])

  const internalUserColors = useMemo(() => {
    return Object.fromEntries(selectedInternalUsers.map((user) => [String(user.id), colors.color8]))
  }, [selectedInternalUsers, colors.color8])

  const handleEmailsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      setEmailsRawInput(raw)
      const emails = raw
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s.length > 0)
      onChange({ externalEmails: emails })
    },
    [onChange, setEmailsRawInput]
  )

  const emailError = useMemo(() => {
    const tokens = emailsRawInput
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
    if (tokens.length === 0) return ''
    return tokens.some((email) => !isEmail(email)) ? t('actionPlan.validation.invalidEmails') : ''
  }, [emailsRawInput, t])

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>{t('actionPlan.manualCreation.step3.title')}</Typography>
      <Typography sx={styles.subtitle}>{t('actionPlan.manualCreation.step3.subtitle')}</Typography>

      <Box sx={styles.section}>
        <Typography sx={styles.sectionTitle}>{t('general.internalUsers')}</Typography>
        <Typography sx={styles.sectionHelper}>{t('actionPlan.manualCreation.step3.selectWhoReceives')}</Typography>

        <Box sx={styles.selectContainer}>
          <Autocomplete
            multiple
            sx={{ width: '100%' }}
            options={internalUserOptions}
            getOptionLabel={(option) => option.fullName}
            value={selectedInternalUsers}
            onChange={handleChangeInternalUsers}
            disableCloseOnSelect
            filterSelectedOptions
            loading={isLoadingInternalUsers}
            renderTags={() => null}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.fullName}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('actionPlan.users')}
                placeholder={t('actionPlan.search')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingInternalUsers ? <CircularProgress color='inherit' size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>

        {selectedInternalUsers.length > 0 && (
          <ChipsList
            value={internalUsersAsItems}
            colorsByChips={internalUserColors}
            handleDelete={handleRemoveInternalUser}
          />
        )}
      </Box>

      <Box sx={styles.section}>
        <Typography sx={styles.sectionTitle}>{t('general.externalUsers')}</Typography>
        <Typography sx={styles.sectionHelper}>{t('actionPlan.manualCreation.step3.enterExternalEmails')}</Typography>

        <Box sx={styles.emailsContainer}>
          <TextField
            fullWidth
            multiline
            minRows={6}
            value={emailsRawInput}
            onChange={handleEmailsChange}
            placeholder={t('actionPlan.emailPlaceholder')}
            error={!!emailError || !!errors.externalEmails}
            helperText={emailError || errors.externalEmails}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default Step3Notifications
